import { MeasureTextWidth, wrapLines } from '@lite-workspace/text-layout';

import { QUOTE_LIMIT_MAX, QUOTE_LIMIT_MIN } from '../quotes.dto';

/**
 * Средняя доля ширины глифа от кегля. Файлы шрифтов сюда не грузятся (и не должны: потолок длины
 * цитаты считается один раз на смену эталона), поэтому ширина берётся усреднённой — 0.6 em: наименьшая доля, при
 * которой оценка не превышает живой замер строк редактора (`frameFitOf`) на кириллице гротесков дизайн-паков.
 */
const AVERAGE_GLYPH_ADVANCE_RATIO = 0.6;

/** Межстрочный множитель, когда образец его не задал. */
const FALLBACK_LINE_HEIGHT_RATIO = 1.2;

/** Эталонная фраза-мерка: смесь коротких и длинных слов, по ней меряется вместимость строки. */
const REFERENCE_ALPHABET = 'Съешь ещё этих мягких французских булок да выпей чаю ';

/** Потолок длины мерки: вместимость всё равно упирается в QUOTE_LIMIT_MAX, дальше мерить незачем. */
const REFERENCE_MAX_CHARS = QUOTE_LIMIT_MAX * 2;

/** Рамка цитаты: ширина и высота в тех же единицах, что и кегль. */
export interface QuoteCapacityRect {
  readonly width: number;
  readonly height: number;
}

/** Шрифт мерки: кегль, которым печатается строка (уже с индексом), и то, что его окружает. */
export interface QuoteCapacityFont {
  readonly fontSize: number;
  /** Номинальный кегль рамки: трекинг задан в px при нём. Без него трекинг — сырой px. */
  readonly nominalFontSize?: number;
  /** Межстрочный множитель; без него — `FALLBACK_LINE_HEIGHT_RATIO`. */
  readonly lineHeight?: number;
  /** Трекинг, px при номинальном кегле. */
  readonly letterSpacing?: number;
  /** Растяжение строки по X. */
  readonly horizontalScale?: number;
}

// На валидной рамке пол не срабатывает (первый символ мерки всегда встаёт в строку): он страхует нечисловую геометрию.
function clampToLimitBounds(capacity: number): number {
  return Math.min(QUOTE_LIMIT_MAX, Math.max(QUOTE_LIMIT_MIN, capacity));
}

/**
 * Вместимость рамки цитаты в символах: сколько строк влезает по высоте × сколько символов мерки влезает в строку
 * по ширине. Одна эвристика на бэкенд (потолок длины, синк рамки) и раскладку серии карточек.
 */
export function estimateQuoteCapacity(rect: QuoteCapacityRect, font: QuoteCapacityFont): number | undefined {
  const { width, height } = rect;
  const { fontSize } = font;
  const nominalFontSize = font.nominalFontSize ?? 0;
  if (width <= 0 || height <= 0 || fontSize <= 0) {
    return undefined;
  }

  const lineHeightRatio: number =
    typeof font.lineHeight === 'number' && font.lineHeight > 0 ? font.lineHeight : FALLBACK_LINE_HEIGHT_RATIO;
  const lineCount: number = Math.max(1, Math.floor(height / (lineHeightRatio * fontSize)));

  // Рамка цитаты — блочный текст: рендер растягивает по X строку целиком, вместе с трекингом
  // (`glyph-layout-backend.ts`, `lineAdvanceOfSegs`); трекинг после растяжения — только у текста на кривой.
  // `letterSpacing` — px при номинальном кегле, то есть доля кегля: мерка на потолке autoFit растит его
  // вместе с глифом (так же трекинг ведут `auto-fit.ts` и `svg-text-generator.ts`), индекс ужимает его
  // вместе с глифом (`glyph-layout-backend.ts`). Без номинала доли нет — берётся сырой px.
  // Отрицательный трекинг не расширяет мерку.
  // Поправки кернинга внутри ранов эвристика не моделирует: они мельче погрешности среднего глифа.
  const rawLetterSpacing: number = font.letterSpacing ?? 0;
  const letterSpacing: number = Math.max(
    0,
    nominalFontSize > 0 ? rawLetterSpacing * (fontSize / nominalFontSize) : rawLetterSpacing,
  );
  const glyphAdvance: number = (fontSize * AVERAGE_GLYPH_ADVANCE_RATIO + letterSpacing) * (font.horizontalScale ?? 1);
  const measure: MeasureTextWidth = (text: string): number => [...text].length * glyphAdvance;

  const charsNeeded: number = Math.min(
    REFERENCE_MAX_CHARS,
    Math.ceil((lineCount * width) / glyphAdvance) + REFERENCE_ALPHABET.length,
  );
  const reference: string = REFERENCE_ALPHABET.repeat(Math.ceil(charsNeeded / REFERENCE_ALPHABET.length));

  const capacity: number = wrapLines([reference], measure, width)
    .slice(0, lineCount)
    .reduce((sum: number, line: string) => sum + [...line.trim()].length, 0);

  return clampToLimitBounds(capacity);
}
